import { TooltipProvider } from '../ui/tooltip';
import { MockAuthProvider } from './mock-auth-provider';
import { IS_DEBUG } from '@/client/confs/app';
import { useLocaleStore } from '@/client/hooks/use-locale';
import { useTheme } from '@/client/hooks/use-theme';
import { ReactQueryDevtoolsProduction } from '@/client/utils/dev';
import { persistOptions, queryClient } from '@/client/utils/query-client';
import { DirectionProvider } from '@radix-ui/react-direction';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { lazy, type ReactNode } from 'react';
import useMedia from 'react-use/lib/useMedia';

// Check if mock mode is enabled
const USE_MOCK = import.meta.env.VITE_ENABLE_MOCK === 'true';

// Lazy-load the wallet stack (reown/wagmi/privy + createAppKit side effects)
// so mock builds never evaluate those chunks; see wallet-stack.tsx
const WalletStack = lazy(() => import('./wallet-stack'));

// Warm the wallet chunk during idle time at app boot so createAppKit isn't the
// cold-load bottleneck when the user first needs auth/trade. Skipped in mock
// builds (they never render WalletStack). Runs once at module load.
if (!USE_MOCK && typeof window !== 'undefined') {
  const prefetch = () => import('./wallet-stack');
  if (typeof requestIdleCallback !== 'undefined') requestIdleCallback(prefetch);
  else setTimeout(prefetch, 1500);
}

const ReactQueryDevtools = () => {
  const isDesktop = useMedia('(min-width: 768px)');
  if (IS_DEBUG) {
    return <ReactQueryDevtoolsProduction initialIsOpen={false} buttonPosition={isDesktop ? 'bottom-left' : 'bottom-left'} />;
  }
  return null;
};

export const AppProviders = ({ children }: { children: ReactNode }) => {
  useTheme();
  // Radix primitives (Select/DropdownMenu/Menu keyboard nav + popper placement)
  // default to 'ltr' internally and never read document.documentElement.dir —
  // they only pick up direction from this provider.
  const direction = useLocaleStore(state => state.direction);

  if (USE_MOCK) {
    return (
      <DirectionProvider dir={direction}>
        <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <ReactQueryDevtools />
          <MockAuthProvider>
            <TooltipProvider delayDuration={100}>{children}</TooltipProvider>
          </MockAuthProvider>
        </PersistQueryClientProvider>
      </DirectionProvider>
    );
  }

  return (
    <DirectionProvider dir={direction}>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <ReactQueryDevtools />
        <WalletStack>
          <TooltipProvider delayDuration={100}>{children}</TooltipProvider>
        </WalletStack>
      </PersistQueryClientProvider>
    </DirectionProvider>
  );
};

export default AppProviders;
